import React, { useState } from "react";
import {
  UsersIcon,
  HeartIcon,
  CursorArrowRaysIcon,
  ShoppingCartIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { useMetricsOverview } from "../hooks/useMetrics";
import { usePlatformConnections } from "../hooks/usePlatforms";
import { useCampaigns } from "../hooks/useCampaigns";
import MetricCard from "../components/MetricCard";
import PlatformCard from "../components/PlatformCard";
import ConnectionStatusDashboard from "../components/ConnectionStatusDashboard";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorMessage from "../components/ErrorMessage";

const Dashboard = () => {
  const [selectedTimeRange, setSelectedTimeRange] = useState("7d");
  const [showConnectModal, setShowConnectModal] = useState(false);

  // Fetch data using custom hooks
  const {
    data: metricsData,
    loading: metricsLoading,
    error: metricsError,
  } = useMetricsOverview(selectedTimeRange);
  const {
    connections,
    loading: connectionsLoading,
    error: connectionsError,
    initiateOAuth,
  } = usePlatformConnections();
  const {
    campaigns,
    loading: campaignsLoading,
    error: campaignsError,
  } = useCampaigns("active", 5);

  const timeRanges = [
    { id: "24h", label: "24 Hours" },
    { id: "7d", label: "7 Days" },
    { id: "30d", label: "30 Days" },
    { id: "90d", label: "90 Days" },
  ];

  const availablePlatforms = [
    { id: "facebook", name: "Facebook" },
    { id: "instagram", name: "Instagram" },
    { id: "twitter", name: "Twitter" },
    { id: "linkedin", name: "LinkedIn" },
    { id: "youtube", name: "YouTube" },
    { id: "tiktok", name: "TikTok" },
  ];

  const handleConnectPlatform = async (platform) => {
    const result = await initiateOAuth(platform);
    if (result.success && result.data.authorization_url) {
      window.location.href = result.data.authorization_url;
    } else {
      console.error("Failed to initiate OAuth:", result.error);
    }
  };

  const getConnectionStatus = (platformId) => {
    return connections.find(
      (conn) => conn.platform === platformId && conn.status === "connected",
    );
  };

  if (metricsLoading && connectionsLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">
            Welcome back! Here's your social media overview.
          </p>
        </div>
        <select
          value={selectedTimeRange}
          onChange={(e) => setSelectedTimeRange(e.target.value)}
          className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {timeRanges.map((range) => (
            <option key={range.id} value={range.id}>
              {range.label}
            </option>
          ))}
        </select>
      </div>

      {/* Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricsError ? (
          <div className="col-span-full">
            <ErrorMessage message={metricsError} />
          </div>
        ) : metricsLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
            >
              <LoadingSpinner />
            </div>
          ))
        ) : metricsData ? (
          <>
            <MetricCard
              title="Total Reach"
              value={metricsData.total_reach?.toLocaleString() || "0"}
              change={metricsData.reach_change || "+0%"}
              icon={UsersIcon}
              iconColor="text-blue-600"
              iconBgColor="bg-blue-100"
            />
            <MetricCard
              title="Engagement Rate"
              value={`${metricsData.engagement_rate || 0}%`}
              change={metricsData.engagement_change || "+0%"}
              icon={HeartIcon}
              iconColor="text-green-600"
              iconBgColor="bg-green-100"
            />
            <MetricCard
              title="Total Clicks"
              value={metricsData.total_clicks?.toLocaleString() || "0"}
              change={metricsData.clicks_change || "+0%"}
              icon={CursorArrowRaysIcon}
              iconColor="text-purple-600"
              iconBgColor="bg-purple-100"
            />
            <MetricCard
              title="Conversions"
              value={metricsData.conversions?.toLocaleString() || "0"}
              change={metricsData.conversions_change || "+0%"}
              icon={ShoppingCartIcon}
              iconColor="text-orange-600"
              iconBgColor="bg-orange-100"
            />
          </>
        ) : (
          <div className="col-span-full text-center py-8 text-gray-500">
            No metrics data available
          </div>
        )}
      </div>

      {/* Connection Status Dashboard */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Account Connections
          </h2>
          <a
            href="/social-accounts"
            className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center space-x-1"
          >
            <span>Manage All</span>
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </a>
        </div>

        {connectionsError ? (
          <ErrorMessage message={connectionsError} />
        ) : connectionsLoading ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
            <LoadingSpinner />
          </div>
        ) : (
          <ConnectionStatusDashboard
            accounts={connections || []}
            onRefresh={() => window.location.reload()}
            isRefreshing={false}
          />
        )}
      </div>

      {/* Platform Connections */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Quick Connect</h2>
          <button
            onClick={() => setShowConnectModal(true)}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
          >
            <PlusIcon className="w-4 h-4" />
            <span>Connect Platform</span>
          </button>
        </div>

        {connectionsError ? (
          <ErrorMessage message={connectionsError} />
        ) : connectionsLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {availablePlatforms.map((platform) => {
              const connection = getConnectionStatus(platform.id);
              return (
                <PlatformCard
                  key={platform.id}
                  platform={platform.id}
                  isConnected={!!connection}
                  followers={connection?.follower_count}
                  growth={connection?.growth_rate}
                  onClick={() =>
                    !connection && handleConnectPlatform(platform.id)
                  }
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Campaigns */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">
            Recent Campaigns
          </h2>
          <button className="text-primary-600 hover:text-primary-700 text-sm font-medium">
            View All
          </button>
        </div>

        {campaignsError ? (
          <ErrorMessage message={campaignsError} />
        ) : campaignsLoading ? (
          <LoadingSpinner />
        ) : campaigns.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="pb-3">Campaign</th>
                  <th className="pb-3">Status</th>
                  <th className="pb-3">Platforms</th>
                  <th className="pb-3">Budget</th>
                  <th className="pb-3">Performance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {campaigns.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-gray-50">
                    <td className="py-4">
                      <div className="font-medium text-gray-900">
                        {campaign.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {campaign.description}
                      </div>
                    </td>
                    <td className="py-4">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          campaign.status === "active"
                            ? "bg-green-100 text-green-800"
                            : campaign.status === "scheduled"
                              ? "bg-blue-100 text-blue-800"
                              : campaign.status === "paused"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {campaign.status}
                      </span>
                    </td>
                    <td className="py-4">
                      <div className="flex space-x-1">
                        {campaign.platforms?.map((platform) => (
                          <span
                            key={platform}
                            className="text-xs bg-gray-100 px-2 py-1 rounded capitalize"
                          >
                            {platform}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-4 text-gray-900">
                      ${campaign.budget?.toLocaleString() || 0}
                    </td>
                    <td className="py-4">
                      <div className="text-sm text-gray-900">
                        {campaign.performance?.impressions?.toLocaleString() ||
                          0}{" "}
                        impressions
                      </div>
                      <div className="text-xs text-gray-500">
                        {campaign.performance?.clicks || 0} clicks
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No campaigns found. Create your first campaign to get started.
          </div>
        )}
      </div>

      {/* Connect Platform Modal */}
      {showConnectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full m-4 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Connect Platform
              </h3>
              <button
                onClick={() => setShowConnectModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                ✕
              </button>
            </div>

            <p className="text-gray-600 mb-4">Select a platform to connect:</p>

            <div className="grid grid-cols-2 gap-3">
              {availablePlatforms
                .filter((platform) => !getConnectionStatus(platform.id))
                .map((platform) => (
                  <button
                    key={platform.id}
                    onClick={() => {
                      handleConnectPlatform(platform.id);
                      setShowConnectModal(false);
                    }}
                    className="p-3 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors text-left"
                  >
                    <div className="font-medium text-gray-900 capitalize">
                      {platform.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      Connect your {platform.name} account
                    </div>
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
